// Pagamentos da organização — operador confirma manualmente Pix recebidos.
// Quando confirma: a compra vira "paid", o ledger ganha +N créditos
// e o aluno + professor são notificados.
//
// UI: lista compacta clicável → modal de detalhe com Pix copia-e-cola,
// QR (visual), botões de copiar (txid, e2eId, pix), compartilhar (whatsapp,
// email, link). Pagamento manual e cancelar ficam dentro do modal.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  CreditCard,
  ExternalLink,
  Link2,
  Loader2,
  Mail,
  MessageCircle,
  PieChart,
  RefreshCw,
  Share2,
  TrendingUp,
  X
} from "lucide-react";
import { Checkbox } from "@app/ui/components/checkbox";
import { generateQrWithLogoSvg } from "@app/ui/lib/qr-with-logo";
import { api, type CompraAdminRow } from "../api";
import { openAlert, openConfirm, openPrompt } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const items = signal<CompraAdminRow[]>([]);
const tab = signal<"pending" | "paid" | "all">("pending");
const opened = signal<CompraAdminRow | null>(null);

interface Summary {
  kpis: {
    receitaMesCents: number;
    receitaMesCount: number;
    pendenteCents: number;
    pendenteCount: number;
    inadimplenteCount: number;
  };
  inadimplentes: Array<{
    id: string;
    alunoUserId: string;
    alunoName: string;
    alunoEmail: string;
    pacoteNomeSnap: string;
    valorCents: number;
    createdAt: string;
    diasAtraso: number;
    disciplinaName: string;
    centroCusto: string | null;
  }>;
  porCentroCusto: Array<{
    centroCusto: string;
    receitaCents: number;
    count: number;
  }>;
}

const summary = signal<Summary | null>(null);

async function loadSummary() {
  try {
    summary.value = await api.pagamentosSummary();
  } catch {
    // não bloqueia a tela principal
  }
}

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listPagamentos(tab.value);
    items.value = r.compras;
    phase.value = "ready";
  } catch (e) {
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function PagamentosView() {
  useEffect(() => {
    load();
    loadSummary();
  }, []);
  useEffect(() => {
    load();
  }, [tab.value]);

  const list = items.value;

  return (
    <div class="space-y-5 px-8">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <CreditCard className="size-6 text-primary" />
            Pagamentos
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Pix recebidos manualmente pela organização. Clique para ver detalhes,
            copiar dados ou compartilhar o pagamento.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <RefreshCw className="size-3.5" />
          Atualizar
        </button>
      </header>

      {summary.value && <DashboardCards s={summary.value} />}

      <div class="flex items-center gap-1 rounded-md border border-border bg-card/40 p-1 text-xs">
        <TabBtn k="pending" current={tab.value} label="Pendentes" onPick={() => (tab.value = "pending")} />
        <TabBtn k="paid" current={tab.value} label="Pagos" onPick={() => (tab.value = "paid")} />
        <TabBtn k="all" current={tab.value} label="Todos" onPick={() => (tab.value = "all")} />
      </div>

      {phase.value === "loading" && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p class="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {errMsg.value}
          </p>
        </div>
      )}

      {phase.value === "ready" && list.length === 0 && (
        <p class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center text-sm text-muted-foreground">
          Nenhum pagamento {tab.value === "pending" ? "pendente" : tab.value === "paid" ? "pago" : "encontrado"}.
        </p>
      )}

      {phase.value === "ready" && list.length > 0 && (
        <ul class="space-y-2">
          {list.map((c) => (
            <PagamentoRow key={c.id} c={c} onOpen={() => (opened.value = c)} />
          ))}
        </ul>
      )}

      {opened.value && (
        <PagamentoDetailModal
          c={opened.value}
          onClose={() => (opened.value = null)}
          onChanged={() => {
            opened.value = null;
            load();
          }}
        />
      )}
    </div>
  );
}

function TabBtn({
  k,
  current,
  label,
  onPick
}: {
  k: "pending" | "paid" | "all";
  current: string;
  label: string;
  onPick: () => void;
}) {
  const active = k === current;
  return (
    <button
      type="button"
      onClick={onPick}
      class={`flex-1 rounded px-3 py-1.5 font-medium transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-accent"
      }`}
    >
      {label}
    </button>
  );
}

function PagamentoRow({
  c,
  onOpen
}: {
  c: CompraAdminRow;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        class="block w-full rounded-md border border-border bg-card/60 p-4 text-left transition-colors hover:bg-accent/30"
      >
        <div class="flex flex-wrap items-start gap-3">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="text-sm font-bold">{c.pacoteNomeSnap}</span>
              <StatusBadge status={c.status} />
            </div>
            <div class="mt-1 grid gap-x-3 text-[11px] text-muted-foreground md:grid-cols-2">
              <div>
                <span class="font-semibold text-foreground">Aluno:</span> {c.alunoName}{" "}
                <span class="opacity-60">({c.alunoEmail})</span>
              </div>
              <div>
                <span class="font-semibold text-foreground">Matéria:</span>{" "}
                {c.disciplinaName}
              </div>
              <div>
                <span class="font-semibold text-foreground">Pacote:</span>{" "}
                {c.numAulasSnap} aulas · {formatBRL(c.valorCents)}
              </div>
              <div>
                <span class="font-semibold text-foreground">Criado:</span>{" "}
                {new Date(c.createdAt).toLocaleString("pt-BR")}
              </div>
            </div>
          </div>
          <span class="shrink-0 self-center rounded-md border border-border bg-background px-2 py-1 text-[10px] font-bold text-muted-foreground">
            Ver detalhes
          </span>
        </div>
      </button>
    </li>
  );
}

function PagamentoDetailModal({
  c,
  onClose,
  onChanged
}: {
  c: CompraAdminRow;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [e2eId, setE2eId] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [qrSvg, setQrSvg] = useState<string>("");

  useEffect(() => {
    if (c.status !== "pending" || !c.pixBrcode) {
      setQrSvg("");
      return;
    }
    let cancelled = false;
    generateQrWithLogoSvg(c.pixBrcode, { margin: 1 })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, [c.pixBrcode, c.status]);

  async function ensureLink(): Promise<string | null> {
    if (linkUrl) return linkUrl;
    setGeneratingLink(true);
    try {
      const r = await api.getChargeLink(c.id);
      setLinkUrl(r.url);
      return r.url;
    } catch (e) {
      await openAlert({
        title: "Não foi possível gerar o link",
        message: friendlyError(e),
        danger: true
      });
      return null;
    } finally {
      setGeneratingLink(false);
    }
  }

  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  }

  async function confirmPayment() {
    const ok = await openConfirm({
      title: "Confirmar pagamento manualmente?",
      message: notify
        ? "O aluno será notificado da confirmação. Use só se você tem certeza de que o pagamento foi recebido."
        : "Sem notificação ao aluno. Use só se você tem certeza de que o pagamento foi recebido.",
      confirmLabel: "Confirmar pagamento"
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.confirmarPagamento(c.id, e2eId.trim() || undefined, notify);
      onChanged();
    } catch (e) {
      await openAlert({
        title: "Falha ao confirmar pagamento",
        message: friendlyError(e),
        danger: true
      });
    } finally {
      setBusy(false);
    }
  }

  async function cancelCompra() {
    const reason = await openPrompt({
      title: "Cancelar cobrança?",
      message: "A cobrança será marcada como cancelada e o aluno será notificado. Opcional: explique o motivo (fica registrado no histórico).",
      placeholder: "Motivo (opcional)",
      confirmLabel: "Cancelar cobrança",
      danger: true,
      multiline: true,
      maxLength: 500
    });
    if (reason === null) return;
    setBusy(true);
    try {
      await api.cancelarPagamento(c.id, reason);
      onChanged();
    } catch (e) {
      await openAlert({
        title: "Falha ao cancelar",
        message: friendlyError(e),
        danger: true
      });
    } finally {
      setBusy(false);
    }
  }

  // Mensagem padrão pra compartilhar com aluno. Inclui o link público quando
  // disponível — é a forma profissional de compartilhar (com identidade da
  // organização). Pix copia-e-cola é fallback.
  function buildShareMsg(includeLink: string | null): string {
    const intro = `Olá, ${c.alunoName.split(" ")[0]}! Pagamento do pacote ${c.pacoteNomeSnap} (${formatBRL(c.valorCents)}).`;
    if (includeLink) {
      return `${intro}\n\nAcesse pra pagar:\n${includeLink}`;
    }
    return `${intro}\n\nPix copia-e-cola:\n${c.pixBrcode}\n\nIdentificador (txid): ${c.txid}`;
  }

  async function shareWhatsApp() {
    const link = await ensureLink();
    const msg = buildShareMsg(link);
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }
  async function shareEmail() {
    const link = await ensureLink();
    const msg = buildShareMsg(link);
    const subject = `Pagamento: ${c.pacoteNomeSnap}`;
    const url = `mailto:${encodeURIComponent(c.alunoEmail)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(msg)}`;
    window.location.href = url;
  }
  async function copyLink() {
    const link = await ensureLink();
    if (link) copy(link, "link");
  }
  async function openLink() {
    const link = await ensureLink();
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  }

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-base font-bold">{c.pacoteNomeSnap}</h2>
              <StatusBadge status={c.status} />
            </div>
            <p class="mt-0.5 text-xs text-muted-foreground">
              {c.alunoName} · {c.disciplinaName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-4 overflow-y-auto px-6 py-4 text-sm">
          {/* Resumo */}
          <section class="rounded-md border border-border bg-background/60 p-3">
            <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Detalhes
            </h3>
            <dl class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <DetailItem label="Aluno" value={c.alunoName} />
              <DetailItem label="E-mail" value={c.alunoEmail} mono />
              <DetailItem label="Pacote" value={`${c.numAulasSnap} aulas`} />
              <DetailItem label="Valor" value={formatBRL(c.valorCents)} bold />
              <DetailItem
                label="Criado em"
                value={new Date(c.createdAt).toLocaleString("pt-BR")}
              />
              {c.paidAt && (
                <DetailItem
                  label="Pago em"
                  value={new Date(c.paidAt).toLocaleString("pt-BR")}
                />
              )}
            </dl>
          </section>

          {/* Identificadores copiáveis */}
          <section>
            <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Identificadores
            </h3>
            <div class="space-y-1.5">
              <CopyField
                label="txid"
                value={c.txid}
                copied={copied === "txid"}
                onCopy={() => copy(c.txid, "txid")}
              />
              {c.e2eId && (
                <CopyField
                  label="e2eId"
                  value={c.e2eId}
                  copied={copied === "e2e"}
                  onCopy={() => copy(c.e2eId!, "e2e")}
                />
              )}
            </div>
          </section>

          {/* Link público de pagamento — gera sob demanda */}
          <section>
            <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Link público de pagamento
            </h3>
            {linkUrl ? (
              <div class="space-y-1.5">
                <div class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2 py-1.5">
                  <Link2 className="size-3.5 text-primary" />
                  <code class="min-w-0 flex-1 truncate font-mono text-[11px]">
                    {linkUrl}
                  </code>
                  <button
                    type="button"
                    onClick={() => copy(linkUrl, "link")}
                    title="Copiar link"
                    aria-label="Copiar link"
                    class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    {copied === "link" ? (
                      <Check className="size-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={openLink}
                    title="Abrir link"
                    aria-label="Abrir link"
                    class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <ExternalLink className="size-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={ensureLink}
                disabled={generatingLink}
                class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50"
              >
                {generatingLink ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Link2 className="size-3.5" />
                )}
                Gerar link público
              </button>
            )}
            <p class="mt-1 text-[10px] text-muted-foreground">
              Página com branding da sua organização. Pode compartilhar por
              WhatsApp, e-mail ou copiar.
            </p>
          </section>

          {/* Pix copia-e-cola (sempre exibido, mas só pendente faz sentido) */}
          {c.status === "pending" && (
            <section>
              {qrSvg && (
                <div class="mb-3 grid place-items-center">
                  <div
                    class="w-44 rounded-md border border-border bg-white p-2"
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                </div>
              )}
              <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Pix copia-e-cola
              </h3>
              <div class="relative">
                <code class="block max-h-32 overflow-y-auto break-all rounded-md border border-border bg-background/60 px-3 py-2 pr-12 font-mono text-[10px] leading-relaxed">
                  {c.pixBrcode}
                </code>
                <button
                  type="button"
                  onClick={() => copy(c.pixBrcode, "pix")}
                  title="Copiar Pix"
                  aria-label="Copiar Pix"
                  class="absolute right-2 top-2 grid size-8 place-items-center rounded-md border border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <Copy className="size-3.5" />
                </button>
              </div>
              {copied === "pix" && (
                <p class="mt-1 text-[10px] text-emerald-700 dark:text-emerald-400">
                  Pix copiado!
                </p>
              )}
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={shareWhatsApp}
                  class="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-400"
                >
                  <MessageCircle className="size-3.5" />
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={shareEmail}
                  class="inline-flex items-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-500/20 dark:text-blue-400"
                >
                  <Mail className="size-3.5" />
                  E-mail
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const link = await ensureLink();
                    copy(buildShareMsg(link), "msg");
                  }}
                  class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
                >
                  <Share2 className="size-3.5" />
                  {copied === "msg" ? "Mensagem copiada" : "Copiar mensagem"}
                </button>
              </div>
            </section>
          )}

          {/* Confirmar pagamento (pendente) */}
          {c.status === "pending" && (
            <section class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
              <h3 class="mb-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                Confirmar recebimento
              </h3>
              <p class="mb-2 text-[11px] text-muted-foreground">
                Confirme após verificar o crédito de{" "}
                <strong>{formatBRL(c.valorCents)}</strong> no extrato do banco.
                Se tiver o "End-to-End ID", cole abaixo (opcional).
              </p>
              <input
                type="text"
                value={e2eId}
                onInput={(e) => setE2eId((e.target as HTMLInputElement).value)}
                placeholder="e2eId (opcional)"
                title="e2eId"
                aria-label="e2eId"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs font-mono outline-none focus:border-primary"
              />
              <label class="mt-2 inline-flex items-center gap-2 text-[11px]">
                <Checkbox
                  checked={notify}
                  onCheckedChange={(v) => setNotify(v === true)}
                />
                Notificar aluno e professor
              </label>
              <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelCompra}
                  disabled={busy}
                  class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-red-500/10 hover:text-red-600 disabled:opacity-50"
                >
                  <X className="size-3.5" />
                  Cancelar cobrança
                </button>
                <button
                  type="button"
                  onClick={confirmPayment}
                  disabled={busy}
                  class="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="size-3.5" />
                  )}
                  Marcar como pago
                </button>
              </div>
            </section>
          )}
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function CopyField({
  label,
  value,
  copied,
  onCopy
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2 py-1.5">
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <code class="min-w-0 flex-1 truncate font-mono text-[11px]">{value}</code>
      <button
        type="button"
        onClick={onCopy}
        title={`Copiar ${label}`}
        aria-label={`Copiar ${label}`}
        class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono,
  bold
}: {
  label: string;
  value: string;
  mono?: boolean;
  bold?: boolean;
}) {
  return (
    <>
      <dt class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd
        class={`truncate ${mono ? "font-mono text-[11px]" : ""} ${
          bold ? "font-bold" : ""
        }`}
      >
        {value}
      </dd>
    </>
  );
}

function DashboardCards({ s }: { s: Summary }) {
  const k = s.kpis;
  return (
    <section class="space-y-4">
      {/* KPI strip */}
      <div class="grid gap-3 sm:grid-cols-3">
        <KpiBigBox
          icon={TrendingUp}
          label="Receita do mês"
          value={formatBRL(k.receitaMesCents)}
          sub={`${k.receitaMesCount} pagamento${k.receitaMesCount === 1 ? "" : "s"}`}
          tone="emerald"
        />
        <KpiBigBox
          icon={Clock}
          label="A receber"
          value={formatBRL(k.pendenteCents)}
          sub={`${k.pendenteCount} pendente${k.pendenteCount === 1 ? "" : "s"}`}
          tone="amber"
        />
        <KpiBigBox
          icon={AlertTriangle}
          label="Inadimplentes (>7d)"
          value={String(k.inadimplenteCount)}
          sub={k.inadimplenteCount === 0 ? "Tudo em dia" : "Atenção"}
          tone={k.inadimplenteCount > 0 ? "red" : "muted"}
        />
      </div>

      {/* Inadimplentes em destaque */}
      {s.inadimplentes.length > 0 && (
        <div class="rounded-md border border-red-500/30 bg-red-500/5 p-4">
          <h3 class="mb-2 flex items-center gap-2 text-sm font-bold text-red-700 dark:text-red-400">
            <AlertTriangle className="size-4" />
            Inadimplentes ({s.inadimplentes.length})
          </h3>
          <ul class="space-y-1">
            {s.inadimplentes.map((i) => (
              <li
                key={i.id}
                onClick={() => {
                  // tenta encontrar a compra na lista carregada
                  const found = items.value.find((x) => x.id === i.id);
                  if (found) opened.value = found;
                }}
                class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-1.5 text-xs cursor-pointer hover:bg-accent/30"
              >
                <span class="font-semibold">{i.alunoName}</span>
                <span class="text-muted-foreground">·</span>
                <span class="truncate">{i.pacoteNomeSnap}</span>
                <span class="text-muted-foreground">·</span>
                <span class="font-mono tabular-nums">
                  {formatBRL(i.valorCents)}
                </span>
                <span class="ml-auto rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold text-red-700 dark:text-red-400">
                  {i.diasAtraso}d em aberto
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Por centro de custo */}
      {s.porCentroCusto.length > 0 && (
        <div class="rounded-md border border-border bg-card/40 p-4">
          <h3 class="mb-2 flex items-center gap-2 text-sm font-bold">
            <PieChart className="size-4 text-primary" />
            Receita por centro de custo (mês)
          </h3>
          <ul class="space-y-1">
            {s.porCentroCusto.map((cc) => (
              <li
                key={cc.centroCusto}
                class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-1.5 text-xs"
              >
                <span class="font-semibold">{cc.centroCusto}</span>
                <span class="ml-auto tabular-nums text-muted-foreground">
                  {cc.count}× ·
                </span>
                <span class="tabular-nums font-bold">
                  {formatBRL(cc.receitaCents)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function KpiBigBox({
  icon: Icon,
  label,
  value,
  sub,
  tone
}: {
  icon: any;
  label: string;
  value: string;
  sub: string;
  tone: "emerald" | "amber" | "red" | "muted";
}) {
  const toneMap = {
    emerald:
      "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400",
    red: "border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-400",
    muted: "border-border bg-card/40 text-muted-foreground"
  } as const;
  return (
    <div class={`rounded-md border px-4 py-3 ${toneMap[tone]}`}>
      <div class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider opacity-90">
        <Icon className="size-3.5" />
        {label}
      </div>
      <div class="mt-1 text-2xl font-extrabold tabular-nums text-foreground">
        {value}
      </div>
      <div class="text-[11px] opacity-80">{sub}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: CompraAdminRow["status"] }) {
  const map = {
    pending: { label: "Pendente", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400", icon: Clock },
    paid: { label: "Pago", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", icon: Check },
    canceled: { label: "Cancelado", cls: "bg-muted text-muted-foreground", icon: X },
    expired: { label: "Expirado", cls: "bg-muted text-muted-foreground", icon: Clock }
  } as const;
  const m = map[status];
  const Icon = m.icon;
  return (
    <span class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${m.cls}`}>
      <Icon className="size-3" />
      {m.label}
    </span>
  );
}
